import { z } from 'zod';

// Plan types
export const planTierSchema = z.enum(['starter', 'pro']);
export type PlanTier = z.infer<typeof planTierSchema>;

export const billingCycleSchema = z.enum(['monthly', 'yearly']);
export type BillingCycle = z.infer<typeof billingCycleSchema>;

export const subscriptionStatusSchema = z.enum([
  'pending',  // User registered but hasn't paid yet (paywall)
  'active',
  'past_due',
  'canceled',
  'expired',
  'paused',
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

// Subscription source (Google Play vs Manual)
export const subscriptionSourceSchema = z.enum(['google_play', 'manual']);
export type SubscriptionSource = z.infer<typeof subscriptionSourceSchema>;

// Usage types for quota tracking
export const usageTypeSchema = z.enum(['ai_uses', 'clients', 'diagnostics']);
export type UsageType = z.infer<typeof usageTypeSchema>;

// Plan limits
export const planLimitsSchema = z.object({
  storage: z.number(), // bytes
  clients: z.number(), // -1 = unlimited
  diagnostics: z.number(), // -1 = unlimited
  aiUsesPerMonth: z.number(), // -1 = unlimited (fair use)
  portalBranding: z.boolean(),
  prioritySupport: z.boolean(),
});
export type PlanLimits = z.infer<typeof planLimitsSchema>;

// Subscription record
export const subscriptionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  planTier: planTierSchema,
  billingCycle: billingCycleSchema.nullable(),
  status: subscriptionStatusSchema,
  // Google Play fields
  purchaseToken: z.string().nullable(),
  orderId: z.string().nullable(),
  productId: z.string().nullable(),
  currentPeriodStart: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  canceledAt: z.string().nullable(),
  gracePeriodEndsAt: z.string().nullable(),
  lastVerifiedAt: z.string().nullable(),
  verificationStatus: z.string().nullable(),
  // Manual license fields
  source: subscriptionSourceSchema.default('google_play'),
  manualExpiresAt: z.string().nullable(),
  resellerName: z.string().nullable(),
  resellerNotes: z.string().nullable(),
  activatedBy: z.string().nullable(),
  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Subscription = z.infer<typeof subscriptionSchema>;

// Usage quota
export const usageQuotaSchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  aiUses: z.number(),
  clientsCreated: z.number(),
  diagnosticsCreated: z.number(),
});
export type UsageQuota = z.infer<typeof usageQuotaSchema>;

// API Response: Subscription Status
// Note: subscription can be null if user has no license assigned
export const subscriptionStatusResponseSchema = z.object({
  subscription: subscriptionSchema.nullable(),
  planLimits: planLimitsSchema,
  usage: usageQuotaSchema,
  canAccessPremiumFeatures: z.boolean(),
});
export type SubscriptionStatusResponse = z.infer<typeof subscriptionStatusResponseSchema>;

// API Request: Verify Purchase
export const verifyPurchaseRequestSchema = z.object({
  purchaseToken: z.string(),
  productId: z.string(),
  packageName: z.string(),
});
export type VerifyPurchaseRequest = z.infer<typeof verifyPurchaseRequestSchema>;

// API Response: Verify Purchase
export const verifyPurchaseResponseSchema = z.object({
  success: z.boolean(),
  subscription: subscriptionSchema,
  message: z.string(),
});
export type VerifyPurchaseResponse = z.infer<typeof verifyPurchaseResponseSchema>;

// Google Play RTDN Notification
export const googleRTDNPayloadSchema = z.object({
  version: z.string(),
  packageName: z.string(),
  eventTimeMillis: z.string(),
  subscriptionNotification: z.object({
    version: z.string(),
    notificationType: z.number(),
    purchaseToken: z.string(),
    subscriptionId: z.string(),
  }),
});
export type GoogleRTDNPayload = z.infer<typeof googleRTDNPayloadSchema>;

// Quota check response
export const canUseFeatureResponseSchema = z.object({
  allowed: z.boolean(),
  remaining: z.number().nullable(),
  limit: z.number().nullable(),
});
export type CanUseFeatureResponse = z.infer<typeof canUseFeatureResponseSchema>;
